/* The order's rail card: where the geolocation recorded at creation came from — `/sales/orders/:id`
   only (`Page.js`'s `dp-rail`), not the drawer, which has no room for a map. The drawer still gets
   the fraud signal, just compactly: see `OrderDetail.js`'s "Загальна інформація" row, which reads
   `parseGeo`/`isFakePoint` from here rather than re-deriving them.

   Modelled on `visit/sections.js`'s `GpsSection` — the closest precedent in the app for a map next
   to a list of points inside one card, mock-location handling included. Two things differ on
   purpose: the rail is 320px wide, too narrow for `GpsSection`'s side-by-side map+stats grid, so
   the list sits BELOW the map here, not beside it; and `tracked_geolocation`'s shape is already
   named (`latitude`/`longitude`/`dateTime`/`isFake`/`sendingLocation`), so there is no alias-hunting
   the way `pingsOf()` needs for `visit.meta`'s free-form pings. */

import React, { useMemo } from "react";
import { Alert as AntAlert, Empty, Flex, Tag, Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { StatusIcon } from "../../component/table-cells";
import { ORDERS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { fill, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import LeafletMap from "../points/ext/LeafletMap";

/* The field's own format — not ISO, and the separator is a literal EN DASH (U+2013) with a space
   on each side, confirmed byte-for-byte against the sample data. `dayjs` has no lowercase `yyyy`
   and no implicit non-ISO parsing (see CLAUDE.md), so both the format string and the strict flag
   are load-bearing: without `[–]` the dash reads as an unknown token and without the format at all
   `dayjs()` falls back to `Date.parse`, which returns Invalid Date for this shape. */
const DATE_TIME_FORMAT = "DD-MM-YYYY [–] HH:mm:ss";

/** Tolerant like `visit/sections.js`'s `isMockPing` — a JSONField written by the mobile app, so a
    boolean can arrive as an actual boolean, a number, or a string. */
export const isFakePoint = point => point?.isFake === true || point?.isFake === 1 || point?.isFake === "true";

function parseTimestamp(value) {
  if (!value) return null;
  const parsed = dayjs(value, DATE_TIME_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

/** `tracked_geolocation` is a `JSONField(default=dict)` with no shape enforced anywhere: `{}` and
    `""` on records nothing ever wrote to, the literal string `"string"` on test rows, and a real
    array once the mobile app has actually recorded a point. Same defensive shape as
    `visit/sections.js`'s `parseMeta` — every one of those has to come back as `[]`, not throw.

    ⚠ Sorted by parsed `time`, ascending — the backend gives no ordering guarantee, and the marker
    numbers, the polyline and the list below all have to agree on "1, 2, 3…" being chronological,
    not array order. An entry whose `dateTime` failed to parse (`time: null`) sorts to the END
    rather than the start: an unknown moment is not "first". */
export function parseGeo(value) {
  const list = Array.isArray(value) ? value : typeof value === "string" && value ? tryParseArray(value) : [];
  return list
    .map(entry => {
      if (!entry || typeof entry !== "object") return null;
      const lat = Number(entry.latitude);
      const lng = Number(entry.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        lat,
        lng,
        time: parseTimestamp(entry.dateTime),
        rawTime: entry.dateTime,
        isFake: isFakePoint(entry),
        /* `sendingLocation` — inferred, not confirmed with the backend team: the one ping among
           the batch whose coordinates were actually attached to the order, as opposed to ambient
           pings recorded around the same moment. Flagged here only as a small tag, never as a
           different pin shape/size — an `isFake` + `sendingLocation` point on the SAME entry has
           no combined style today (`.lf-pin--dot.lf-pin--no` is the only fault-tone rule), and a
           new marker kind for an unconfirmed meaning is not worth that gap. */
        sending: entry.sendingLocation === true,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.time ? a.time.valueOf() : Infinity) - (b.time ? b.time.valueOf() : Infinity));
}

function tryParseArray(value) {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/* Fixed `DD.MM.YYYY HH:mm:ss`, not locale-dependent — matching every other timestamp in this
   component (order date, status-changed date), none of which vary by `lang` either. */
const timeLabel = point => (point.time ? point.time.format("DD.MM.YYYY HH:mm:ss") : String(point.rawTime ?? "—"));

/* Haversine, kept local on purpose — one consumer today (this card), and no other screen in the
   app computes a distance at all yet (checked: no `distance`/`haversine` utility and no backend
   field for it exist anywhere). Extracting to `util/` now would commit to an API nobody has asked
   for a second time; do that when a second caller actually shows up. Earth radius in km. */
const EARTH_RADIUS_KM = 6371;
function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = deg => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
}

/** "10,3 км" / "300 м" — `Intl.NumberFormat(localeFor(lang))` is the app's own locale-aware number
    formatting (`overview/View.js`'s `n()`), which is what actually produces the comma for `uk`
    rather than a hardcoded replace. No precedent anywhere in the app for a sub-1km cutoff (this is
    the first distance shown at all), so the rule is the plainest one: under a kilometre reads as
    metres, whole — "0,3 км" is harder to place at a glance than "300 м", and a fault-finding
    distance this short is exactly the case worth being unambiguous about. */
function distanceLabel(km, t, lang) {
  if (km >= 1) {
    const n = new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: 1 }).format(km);
    return fill(t("geo_distance_km"), { n });
  }
  const n = new Intl.NumberFormat(localeFor(lang)).format(Math.round(km * 1000));
  return fill(t("geo_distance_m"), { n });
}

export default function GeoSection({ item, lang }) {
  const t = key => translate(ORDERS_PAGE, key, lang);
  const points = useMemo(() => parseGeo(item?.tracked_geolocation), [item]);
  const fakeCount = points.filter(p => p.isFake).length;

  /* `point.location` is a separate, nullable field on the DETAIL response — an order can have a
     point with no location, or no point at all. Guarded the same way `parseGeo` guards a single
     entry: both coordinates must be finite, never trusted as "present because the object is". */
  const pointLat = Number(item?.point?.location?.latitude);
  const pointLng = Number(item?.point?.location?.longitude);
  const hasPoint = Number.isFinite(pointLat) && Number.isFinite(pointLng);
  const hasTrack = points.length > 0;
  const showMap = hasPoint || hasTrack;

  const markers = useMemo(() => {
    const out = points.map((p, index) => ({
      key: `geo-${index}`,
      lat: p.lat,
      lng: p.lng,
      /* ⚠ NOT `kind: "dot"` — that is `LeafletMap.pin()`'s 12px trail ping (`.lf-pin--dot`,
         `visit/sections.js`'s own GPS track: "small on purpose... they don't label it"), sized
         for a cloud of dozens of unlabelled pings, not a handful of numbered stops. This is the
         one caller that puts a `label` inside a `dot`, which the CSS never sized for — a 1-2
         digit number does not fit an 8px inner circle, confirmed by an actual render, not a
         guess. Omitting `kind` here falls through to the 28px default `.lf-pin`, which already
         carries the white-bold-number styling this needs (see `leaflet.scss`).
         `label` is `LeafletMap.pin()`'s own mechanism for text inside a divIcon. Numbering
         matches the sorted order `parseGeo` produces, the same 1/2/3 the list below shows per
         row. */
      tone: p.isFake ? "no" : undefined,
      label: index + 1,
      title: `${index + 1}. ${timeLabel(p)}`,
    }));
    if (hasPoint) out.push({ key: "point", lat: pointLat, lng: pointLng, kind: "point", title: item?.point?.name });
    return out;
  }, [points, hasPoint, pointLat, pointLng, item?.point?.name]);

  /* The route line — tracked points ONLY, in the same chronological order as the numbers, and
     deliberately excluding the point marker: it is a fixed place, not a stop on the route. Solid
     through a fake point exactly like `visit/sections.js`'s own track — the line traces where the
     device reported being, fake or not; only the dot's colour and the badge say which. */
  const track = useMemo(() => points.map(p => [p.lat, p.lng]), [points]);

  return (
    <div className="dp-card">
      <Typography.Text strong className="dp-sec-title">
        {t("k_tracked_geolocation")}
      </Typography.Text>

      {showMap ? (
        <Flex vertical gap="small">
          {/* Not colour alone (spec/01): a banner with an icon and words, on top of the red dots
             below — the exact wording `visit/sections.js`'s `gps_mock_warning` uses for a
             mock-location ping, own key per page (CLAUDE.md's rule: pages don't cross-reference
             each other's lang groups even for identical copy). */}
          {fakeCount ? <AntAlert type="warning" showIcon message={fill(t("geo_fake_gps_warning"), { n: fakeCount })} /> : null}

          {/* `fitKey` re-arms the fit on every order change — `Page.js` never remounts this
             component across `/sales/orders/:id` navigations, only its `item` prop changes, so
             without a key the map would keep whatever pan/zoom the previous order left it at. */}
          <LeafletMap
            lat={hasPoint ? pointLat : points[0].lat}
            lng={hasPoint ? pointLng : points[0].lng}
            markers={markers}
            track={track}
            height={220}
            fitKey={item?.id}
          />

          {/* Only meaningful once two marker KINDS are actually on the map at once — a lone point
             or a lone track needs no legend, and the fake-point list below already explains the
             red dots on their own.

             No className on either of these two: neither `orders/` nor `returns/` has a stylesheet
             at all, and the spacing and colour both come from antd props already (`Flex gap` and
             `type="secondary"`). A hook with no rule behind it reads as a style that went missing,
             and writing one would mean a new stylesheet plus its import — the `list-page.scss`
             re-emit trap in CLAUDE.md — to restate what the props say. */}
          {hasPoint && hasTrack ? <Typography.Text type="secondary">{t("geo_map_legend")}</Typography.Text> : null}

          {hasTrack ? (
            <Flex vertical gap={4}>
              {points.map((p, index) => {
                const distance = hasPoint ? distanceLabel(haversineKm(pointLat, pointLng, p.lat, p.lng), t, lang) : null;
                return (
                  <Flex key={index} align="center" gap={6} wrap>
                    <Typography.Text type="secondary">{index + 1}.</Typography.Text>
                    {/* A THIRD signal beyond the red dot and the banner above: the same
                       `StatusIcon` every list in the app uses so a fault is never colour-only. */}
                    {p.isFake ? <StatusIcon Icon={WarningOutlined} tone="no" label={t("geo_fake_label")} /> : null}
                    <Typography.Text type={p.isFake ? "danger" : "secondary"}>
                      {timeLabel(p)}
                      {distance ? ` · ${distance}` : ""}
                    </Typography.Text>
                    {/* The visible badge, distinct from the icon's tooltip — asked for by name
                       ("Підроблений GPS"), not inferred from the icon alone. */}
                    {p.isFake ? <Tag color="error">{t("geo_fake_badge")}</Tag> : null}
                    {p.sending ? <Tag>{t("geo_sending_label")}</Tag> : null}
                  </Flex>
                );
              })}
            </Flex>
          ) : null}
        </Flex>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("geo_no_data")} />
      )}
    </div>
  );
}
